export enum ExerciseType {
  MultipleChoice = 'multiple_choice',
  FillInBlanks = 'fill_in_blanks',
  PronunciationPractice = 'pronunciation_practice',
  WriteTranslation = 'write_translation',
  Video = 'video',
  ComprehensionQuestion = 'comprehension_question',
  FillInTheText = 'fill_in_the_text',
  SpeakTheSentence = 'speak_the_sentence',
  WriteTheTranslation = 'write_the_translation',
  AudioRecording = 'audio_recording',
  WatchVideo = 'watch_video',
  WatchLargeVideo = 'watch_large_video',
  DragAndDrop = 'drag_and_drop',
  FillInTheBlanks = 'fill_in_the_blanks',
  FormattedText = 'formatted_text',
  GlossaryDisplay = 'glossary_display'
}

export interface Option {
  id: string;
  text: string;
  isCorrect?: boolean;
}

export interface Exercise {
  id?: string | number;
  data: unknown; // Dados específicos do tipo de exercício
  type: ExerciseType;
  /** false esconde o exercício do aluno. Omissão trata como ativo. */
  isActive?: boolean;
}

// Interfaces para dados específicos de cada tipo de exercício
export interface MultipleChoiceData {
  question: string;
  options: Option[];
  multipleCorrectAnswers?: boolean;
}

export interface FillInBlanksData {
  text: string;
  blanks: string[];
}

export interface PronunciationPracticeData {
  sentences: string[];
  instructions?: string;
  maxDuration?: number;
}

export interface WriteTranslationData {
  originalText: string;
  translatedText: string;
}

export interface VideoData {
  title: string;
  videoUrl?: string;
}

export interface ComprehensionQuestionData {
  question_text: string;
  answer?: string;
}

export interface FormattedTextData {
  content: string;
}

export interface GlossaryTerm {
  id: string;
  term: string;
  definition: string;
  order: number;
}

export interface GlossaryTopic {
  id: string;
  title: string;
  terms: GlossaryTerm[];
  order?: number;
}

export interface GlossaryData {
  title: string;
  topics: GlossaryTopic[];
  // Deprecated: manter terms apenas para compatibilidade de leitura
  terms?: GlossaryTerm[];
}

// Type guards para verificar tipos de dados
export const isMultipleChoiceData = (data: unknown): data is MultipleChoiceData => {
  return typeof data === 'object' && data !== null && 'question' in data && 'options' in data;
};

export const isFillInBlanksData = (data: unknown): data is FillInBlanksData => {
  return typeof data === 'object' && data !== null && 'text' in data && 'blanks' in data;
};

export const isPronunciationPracticeData = (data: unknown): data is PronunciationPracticeData => {
  return typeof data === 'object' && data !== null && 'sentences' in data;
};

export const isWriteTranslationData = (data: unknown): data is WriteTranslationData => {
  return typeof data === 'object' && data !== null && 'originalText' in data && 'translatedText' in data;
};

export const isVideoData = (data: unknown): data is VideoData => {
  return typeof data === 'object' && data !== null && 'title' in data;
};

export const isComprehensionQuestionData = (data: unknown): data is ComprehensionQuestionData => {
  return typeof data === 'object' && data !== null && 'question_text' in data;
};

export const isFormattedTextData = (data: unknown): data is FormattedTextData => {
  return typeof data === 'object' && data !== null && 'content' in data;
};

export const isGlossaryData = (data: unknown): data is GlossaryData => {
  if (typeof data !== 'object' || data === null) return false;
  const d = data as Record<string, unknown>;
  // Aceitar formato novo (topics) ou formato antigo (terms) para compatibilidade
  return 'title' in d && (
    ('topics' in d && Array.isArray(d.topics)) ||
    ('terms' in d && Array.isArray(d.terms))
  );
};

// Helper para obter dados tipados baseado no tipo de exercício
export const getTypedExerciseData = <T>(exercise: Exercise): T | null => {
  if (!exercise.data) return null;
  return exercise.data as T;
};

export interface ExerciseListProps {
  exercises: Exercise[];
  onAdd?: () => void;
  onEdit?: (exerciseId: string | number) => void;
  onDelete?: (exerciseId: string | number) => void;
  context: 'level-test' | 'module';
}

export interface ExerciseDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: (exercise: Exercise) => void;
  initialData?: Exercise;
  context: 'level-test' | 'module';
}

export const exerciseTypeOptions = [
  { value: ExerciseType.MultipleChoice, label: 'Múltipla Escolha' },
  { value: ExerciseType.FillInBlanks, label: 'Preencher Lacunas' },
  { value: ExerciseType.FillInTheBlanks, label: 'Preencher as Lacunas' },
  { value: ExerciseType.PronunciationPractice, label: 'Prática de Pronúncia' },
  { value: ExerciseType.WriteTranslation, label: 'Escrever Tradução' },
  { value: ExerciseType.Video, label: 'Vídeo' },
  { value: ExerciseType.ComprehensionQuestion, label: 'Questão de Compreensão' },
  { value: ExerciseType.FillInTheText, label: 'Preencher o Texto' },
  { value: ExerciseType.SpeakTheSentence, label: 'Falar a Frase' },
  { value: ExerciseType.AudioRecording, label: 'Gravação de Áudio' },
  { value: ExerciseType.WatchVideo, label: 'Assistir Vídeo' },
  { value: ExerciseType.WatchLargeVideo, label: 'Assistir Vídeo Longo' },
  { value: ExerciseType.DragAndDrop, label: 'Arrastar e Soltar' },
  { value: ExerciseType.FormattedText, label: 'Texto Formatado' },
  { value: ExerciseType.GlossaryDisplay, label: 'Glossário Interativo' },
];